import React, { useEffect, useState } from "react";
import { db } from "../firebase/config";
import { collection, getDocs, updateDoc, doc, deleteDoc, setDoc } from "firebase/firestore";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import RichTextField from "../components/TextToolbar";
import ArticleCard from "../components/ArticleCard";
import { useSpecialism } from "../contexts/SpecialismContext";
import type { Article } from "../types/Article";
import type { ExpandableField } from "../types/Article";
import { articleFields } from "../types/Article";
import UnsavedChangesGuard from "../components/UnsavedChangesGuard";

// Helper to generate initial form state
function getInitialFormState(): Article {
  const state: any = {};
  articleFields.forEach(field => {
    // Set sensible defaults based on type
    if (field.type === "text" || field.type === "richtext") {
      state[field.name] = "";
    } else if (field.name === "imageUrls") {
      state.imageUrls = [];
    }
  });
  state.id = undefined; // If you want id to be undefined initially
  return state as Article;
}

type LocationState = {
  editArticle?: Article;
};


type PreviewImage =
  | { type: "uploaded"; url: string }
  | { type: "new"; url: string; file: File };

const Editing: React.FC = () => {
  const location = useLocation<LocationState>();
  const editArticle = location.state?.editArticle;
  const { logout } = useAuth();
  const { specialism } = useSpecialism(); // <-- Add this line
  const { specialismCode } = useSpecialism();

  const [articles, setArticles] = useState<Article[]>([]);
  const [form, setForm] = useState<Article>(getInitialFormState());
  const [editingId, setEditingId] = useState<string | null>(null);

  // --- NEW: Single previewImages state for all images ---
  const [previewImages, setPreviewImages] = useState<PreviewImage[]>([]);
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  const [deleteImageIdx, setDeleteImageIdx] = useState<number | null>(null);
  const [touchStartIdx, setTouchStartIdx] = useState<number | null>(null);
  const [touchOverIdx, setTouchOverIdx] = useState<number | null>(null);

  // Expanded state for "show more/less" per article and field
  const [expanded, setExpanded] = useState<{
    [id: string]: Partial<Record<ExpandableField, boolean>>;
  }>({});

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [redirectId, setRedirectId] = useState<string | null>(null);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };



  useEffect(() => {
    const fetchArticles = async () => {
      const collectionName = specialism === "Oogheelkunde" ? "articles_ohk" : "articles"; // <-- Use specialism
      const querySnapshot = await getDocs(collection(db, collectionName));
      const articlesData: Article[] = [];
      querySnapshot.forEach((docSnap) => {
        articlesData.push({ id: docSnap.id, ...docSnap.data() } as Article);
      });
      setArticles(articlesData);
    };
    fetchArticles();
  }, [specialism]); // <-- Refetch when specialism changes

  // Pre-fill form and previewImages if editArticle is present
  useEffect(() => {
    if (editArticle) {
      const newForm: any = {};
      articleFields.forEach(field => {
        if (field.name === "imageUrls") {
          newForm.imageUrls = editArticle.imageUrls || [];
        } else if (field.name === "id") {
          newForm.id = editArticle.id;
        } else {
          newForm[field.name] = editArticle[field.name as keyof Article] || "";
        }
      });
      setForm(newForm as Article);
      setEditingId(editArticle.id || null);
      setPreviewImages((editArticle.imageUrls || []).map(url => ({ type: "uploaded", url })));
    } else {
      setPreviewImages([]);
    }
  }, [editArticle]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Helper to get preview URL for selected images
  const getPreviewUrl = (file: File) => URL.createObjectURL(file);

  // Handle image selection (append new images to previewImages)
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newImages: PreviewImage[] = Array.from(e.target.files).map(file => ({
        type: "new",
        file,
        url: getPreviewUrl(file),
      }));
      setPreviewImages(prev => [...prev, ...newImages]);
    }
  };

  // --- NEW: handleSubmit returns the article ID ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { id, title, ...articleData } = form;
    let docId = id && id.trim() !== "" ? id.trim() : title.trim().replace(/\s+/g, "_").toLowerCase();

    const storage = getStorage();
    let finalImageUrls: string[] = [];
    for (const img of previewImages) {
      if (img.type === "uploaded") {
        finalImageUrls.push(img.url);
      } else if (img.type === "new") {
        const storageRef = ref(storage, `articles/${docId}/${Date.now()}_${img.file.name}`);
        await uploadBytes(storageRef, img.file);
        const url = await getDownloadURL(storageRef);
        finalImageUrls.push(url);
      }
    }

    const collectionName = specialism === "Oogheelkunde" ? "articles_ohk" : "articles"; // <-- Use specialism

    if (editingId) {
      await updateDoc(doc(db, collectionName, editingId), { title, ...articleData, imageUrls: finalImageUrls });
    } else {
      await setDoc(doc(db, collectionName, docId), { title, ...articleData, imageUrls: finalImageUrls });
    }

    // Refresh articles
    const querySnapshot = await getDocs(collection(db, collectionName)); // <-- Use specialism
    const articlesData: Article[] = [];
    querySnapshot.forEach((docSnap) => {
      articlesData.push({ id: docSnap.id, ...docSnap.data() } as Article);
    });
    setArticles(articlesData);

    // Return the article ID for redirect
    return editingId ? editingId : docId;
  };

const handleEdit = (article: Article) => {
  const newForm: any = {};
  articleFields.forEach(field => {
    if (field.name === "imageUrls") {
      newForm.imageUrls = article.imageUrls || [];
    } else if (field.name === "id") {
      newForm.id = article.id;
    } else {
      newForm[field.name] = article[field.name as keyof Article] || "";
    }
  });
  setForm(newForm as Article);
  setEditingId(article.id || null);
  setPreviewImages(
    (article.imageUrls || []).map(url => ({ type: "uploaded", url }))
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
};

  const handleDelete = async () => {
    if (deleteId) {
      const collectionName = specialism === "Oogheelkunde" ? "articles_ohk" : "articles"; // <-- Use specialism
      await deleteDoc(doc(db, collectionName, deleteId));
      setDeleteId(null);
      // Refresh articles
      const querySnapshot = await getDocs(collection(db, collectionName));
      const articlesData: Article[] = [];
      querySnapshot.forEach((docSnap) => {
        articlesData.push({ id: docSnap.id, ...docSnap.data() } as Article);
      });
      setArticles(articlesData);
    }
  };

  // --- Removed image deletion logic (now handled in handleConfirmDeleteImage) ---

  // Only allow toggling for expandable fields
  const toggleExpand = (id: string, field: ExpandableField) => {
    setExpanded(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: !prev[id]?.[field],
      },
    }));
  };

  function getShortText(text: string, wordLimit: number = 15): string {
    const words = text.split(/\s+/);
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(" ") + "...";
  }

  const labelStyle: React.CSSProperties = {
    fontWeight: "normal",
    color: "#888",
    fontSize: "0.9rem",
    marginBottom: "0px", // even less space
  };
  
  const inputStyle: React.CSSProperties = {
    marginTop: "-10px",
    padding: "12px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "0.9rem",
    resize: "vertical",
    fontFamily: "inherit", // ensure consistent font
  };

  const buttonBase: React.CSSProperties = {
    border: "none",
    borderRadius: "6px",
    fontWeight: "bold",
    cursor: "pointer",
    padding: "6px 16px",
    marginTop: "8px",
  };

  const buttonPrimary: React.CSSProperties = {
    ...buttonBase,
    background: "#1976d2",
    color: "#fff",
    marginRight: "8px",
  };

  const buttonDanger: React.CSSProperties = {
    ...buttonBase,
    background: "#e53935",
    color: "#fff",
  };

  const cardStyle: React.CSSProperties = {
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  };

  const modalOverlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(0,0,0,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  };

  const modalStyle: React.CSSProperties = {
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    padding: "32px",
    minWidth: "320px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  };

  const textMuted: React.CSSProperties = {
    color: "#888",
    fontSize: "0.95rem",
  };

  const showMoreButton: React.CSSProperties = {
    background: "none",
    color: "#1976d2",
    border: "none",
    cursor: "pointer",
    fontSize: "0.95rem",
    marginLeft: "8px",
    padding: 0,
  };

  // Drag-and-drop logic for previewImages
  const handlePreviewDragStart = (idx: number) => setDraggedIdx(idx);
  const handlePreviewDrop = (idx: number) => {
    if (draggedIdx === null || draggedIdx === idx) return;
    const updated = [...previewImages];
    const [removed] = updated.splice(draggedIdx, 1);
    updated.splice(idx, 0, removed);
    setPreviewImages(updated);
    setDraggedIdx(null);
  };

  // Remove image logic
  const handleRemovePreviewImage = (idx: number) => {
    setPreviewImages(prev => prev.filter((_, i) => i !== idx));
  };

  // Delete uploaded image from Firebase Storage
  const handleConfirmDeleteImage = async () => {
    if (deleteImageIdx === null) return;
    const img = previewImages[deleteImageIdx];
    if (img.type === "uploaded") {
      try {
        const storage = getStorage();
        const matches = img.url.match(/\/o\/(.*?)\?alt=media/);
        const path = matches ? decodeURIComponent(matches[1]) : null;
        if (path) {
          const imageRef = ref(storage, path);
          await deleteObject(imageRef);
        }
      } catch (err) {
        // Optionally handle error
      }
    }
    handleRemovePreviewImage(deleteImageIdx);
    setDeleteImageIdx(null);
  };

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const expandableFields: ExpandableField[] = articleFields
    .filter(field => field.type === "richtext")
    .map(field => field.name as ExpandableField);

  // --- NEW: Redirect after submit ---
  useEffect(() => {
    if (redirectId) {
      window.location.href = `/${specialismCode}/${redirectId}`;
    }
  }, [redirectId, specialism]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        background: "#f5f6fa",
        fontFamily: "Roboto, Arial, sans-serif",
        padding: "32px",
        position: "relative",
      }}
    >
      <h2 style={{ fontSize: "2rem", marginBottom: "24px" }}>Edit & Create Articles</h2>
      <form
        onSubmit={handleSubmit}
        style={{
          background: "#fff",
          borderRadius: "18px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          padding: "24px",
          marginBottom: "32px",
          width: "100%",
          maxWidth: "800px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {articleFields
          .filter(field => field.type === "richtext" || field.type === "text") // Only render text/richtext fields
          .filter(field => field.name !== "imageUrls") // Exclude imageUrls or any other array/object fields
          .map(field => (
            field.type === "richtext" ? (
              <RichTextField
                key={field.name}
                name={field.name}
                label={field.label}
                value={typeof form[field.name as keyof Article] === "string" ? (form[field.name as keyof Article] as string) : ""}
                onChange={v => setForm(f => ({ ...f, [field.name as keyof Article]: v }))}
              />
            ) : (
              <input
                key={field.name}
                name={field.name}
                value={typeof form[field.name as keyof Article] === "string" ? (form[field.name as keyof Article] as string) : ""}
                onChange={handleChange}
                placeholder={field.label}
                required={field.required}
                style={inputStyle}
              />
            )
          ))}
        {/* Image upload and preview section */}
        <div style={{ marginBottom: "24px" }}>
          <label style={{ ...labelStyle, display: "block", marginBottom: "8px" }}>
            Add Images
          </label>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleImageChange}
            style={{ ...inputStyle, display: "block", marginBottom: "12px" }}
          />
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "8px" }}>
            {previewImages.map((img, idx) => (
              <div
                key={idx}
                draggable
                onDragStart={() => handlePreviewDragStart(idx)}
                onDragOver={handleDragOver}
                onDrop={() => handlePreviewDrop(idx)}
                style={{
                  position: "relative",
                  width: "80px",
                  height: "80px",
                  borderRadius: "8px",
                  overflow: "hidden",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                  background: "#fafafa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={img.url}
                  alt={`preview-${idx}`}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                <button
                  type="button"
                  onClick={() => setDeleteImageIdx(idx)}
                  style={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    background: "#e53935",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "22px",
                    height: "22px",
                    fontSize: "1rem",
                    cursor: "pointer",
                    zIndex: 2,
                  }}
                  title="Delete image"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <div style={{ fontSize: "0.9rem", color: "#888", marginTop: "4px" }}>
            Drag images to rearrange. Click × to remove.
          </div>
        </div>
        <label style={labelStyle}>Custom ID (optional)</label>
        <input
          name="id"
          value={form.id || ""}
          onChange={handleChange}
          placeholder="Enter custom ID for this article"
          style={inputStyle}
        />
        {/* <ArticleFormFields
          form={form}
          setForm={setForm}
          handleChange={handleChange}
          handleImageChange={handleImageChange}
          previewImages={previewImages}
          setPreviewImages={setPreviewImages}
          draggedIdx={draggedIdx}
          setDraggedIdx={setDraggedIdx}
          handlePreviewDragStart={handlePreviewDragStart}
          handlePreviewDrop={handlePreviewDrop}
          handleRemovePreviewImage={handleRemovePreviewImage}
          deleteImageIdx={deleteImageIdx}
          setDeleteImageIdx={setDeleteImageIdx}
          handleConfirmDeleteImage={handleConfirmDeleteImage}
          isOnline={isOnline}
          editingId={editingId}
        /> */}
        {/* Show offline warning if not online */}
        {!isOnline && (
          <div style={{
            color: "#e53935",
            background: "#fff3f3",
            border: "1px solid #e53935",
            borderRadius: "8px",
            padding: "12px",
            marginBottom: "8px",
            textAlign: "center",
            fontWeight: "bold"
          }}>
            You are offline. Creating or updating articles is disabled.
          </div>
        )}
        <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
          {editingId ? (
            <>
              <button
                type="button"
                style={{
                  background: "#1976d2",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "12px",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  cursor: isOnline ? "pointer" : "not-allowed",
                  opacity: isOnline ? 1 : 0.5,
                }}
                disabled={!isOnline}
                onClick={async () => {
                  const articleId = await handleSubmit(new Event("submit") as any);
                  if (articleId) {
                    setRedirectId(articleId);
                  }
                }}
              >
                Update & View Article
              </button>
              <button
                type="button"
                style={{
                  background: "#1976d2",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "12px",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  cursor: isOnline ? "pointer" : "not-allowed",
                  opacity: isOnline ? 1 : 0.5,
                }}
                disabled={!isOnline}
                onClick={async () => {
                  await handleSubmit(new Event("submit") as any);
                  setForm(getInitialFormState());
                  setEditingId(null);
                  setPreviewImages([]);
                }}
              >
                Update & Create New
              </button>
              <button
                type="button"
                style={{
                  background: "#e53935",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "12px",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  cursor: "pointer",
                  marginLeft: "auto"
                }}
                onClick={() => setDeleteId(editingId)}
              >
                Delete
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                style={{
                  background: "#1976d2",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "12px",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  cursor: isOnline ? "pointer" : "not-allowed",
                  opacity: isOnline ? 1 : 0.5,
                }}
                disabled={!isOnline}
                onClick={async () => {
                  const articleId = await handleSubmit(new Event("submit") as any);
                  if (articleId) {
                    setRedirectId(articleId);
                  }
                }}
              >
                Create & View
              </button>
              <button
                type="button"
                style={{
                  background: "#1976d2",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "12px",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  cursor: isOnline ? "pointer" : "not-allowed",
                  opacity: isOnline ? 1 : 0.5,
                }}
                disabled={!isOnline}
                onClick={async () => {
                  await handleSubmit(new Event("submit") as any);
                  setForm(getInitialFormState());
                  setEditingId(null);
                  setPreviewImages([]);
                }}
              >
                Create & New
              </button>
            </>
          )}
        </div>
      </form>
      {/* Delete image modal */}
      {deleteImageIdx !== null && (
        <div style={modalOverlayStyle}>
          <div style={modalStyle}>
            <p style={{ fontSize: "1.1rem", marginBottom: "24px" }}>
              Are you sure you want to delete this image?
            </p>
            <div style={{ display: "flex", gap: "16px" }}>
              <button
                onClick={handleConfirmDeleteImage}
                style={{ ...buttonDanger, padding: "8px 24px" }}
              >
                Yes, delete
              </button>
              <button
                onClick={() => setDeleteImageIdx(null)}
                style={{ ...buttonPrimary, padding: "8px 24px", marginRight: 0 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete article confirmation modal */}
      {deleteId && (
        <div style={modalOverlayStyle}>
          <div style={modalStyle}>
            <p style={{ fontSize: "1.1rem", marginBottom: "24px" }}>
              Are you sure you want to delete this article?
            </p>
            <div style={{ display: "flex", gap: "16px" }}>
              <button
                onClick={async () => {
                  await handleDelete();
                  setForm(getInitialFormState());
                  setEditingId(null);
                  setPreviewImages([]);
                }}
                style={{ ...buttonDanger, padding: "8px 24px" }}
              >
                Yes, delete
              </button>
              <button
                onClick={() => setDeleteId(null)}
                style={{ ...buttonPrimary, padding: "8px 24px", marginRight: 0 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <div
  style={{
    width: "100%",
    maxWidth: "800px",
    margin: "0 auto",
    boxSizing: "border-box",
    padding: "0 8px",
  }}
>
{/*
  <input
    type="text"
    value={searchTerm}
    onChange={e => setSearchTerm(e.target.value)}
    placeholder="Search articles..."
    style={{
      width: "100%",
      maxWidth: "400px",
      marginBottom: "24px",
      display: "block",
      marginLeft: "auto",
      marginRight: "auto",
      padding: "12px",
      borderRadius: "8px",
      border: "1px solid #ccc",
      fontSize: "1rem",
    }}
  />
  <h3 style={{ marginBottom: "16px" }}>Articles</h3>
  <div style={{ display: "grid", gap: "16px", width: "100%" }}>
    {articles
      .filter(article => {
        const term = searchTerm.trim().toLowerCase();
        return articleFields
          .filter(field => field.type === "text" || field.type === "richtext")
          .some(field => {
            const value = article[field.name as keyof Article];
            return typeof value === "string" && value.toLowerCase().includes(term);
          });
      })
      .map(article => (
        <ArticleCard
          key={article.id}
          article={{ ...article, id: article.id ?? "", subcategory: article.subcategory ?? "" }}
          expanded={expanded}
          toggleExpand={toggleExpand}
          handleEdit={handleEdit}
          setDeleteId={setDeleteId}
          expandableFields={expandableFields}
          getShortText={getShortText}
          showMoreButton={showMoreButton}
          buttonPrimary={buttonPrimary}
          buttonDanger={buttonDanger}
          textMuted={textMuted}
        />
      ))}
  </div>
*/}
</div>
<UnsavedChangesGuard
  form={form}
  previewImages={previewImages}
  handleSubmit={handleSubmit}
  getInitialFormState={getInitialFormState}
  modalOverlayStyle={modalOverlayStyle}
  modalStyle={modalStyle}
  buttonPrimary={buttonPrimary}
  buttonDanger={buttonDanger}
/>
    </div>
  );
};

export default Editing;